/**
 * Portfolio Paper Trading API Service
 * Handles all API calls for paper trading portfolio
 */

const API_BASE_URL = 'http://localhost:8000';
const API_PREFIX = '/api/portfolio-trading';

const getAuthHeaders = () => {
  const token = window.sessionStorage.getItem('indicatorai_token') || window.localStorage.getItem('indicatorai_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getPreferredLanguage = () => {
  const storedLanguage = window.localStorage.getItem('i18nextLng');
  const browserLanguage = window.navigator.language;
  const language = (storedLanguage || browserLanguage || 'en').toLowerCase();
  return language.startsWith('vi') ? 'vi' : 'en';
};

const extractLocalizedMessage = (value) => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const preferredLanguage = getPreferredLanguage();
  const fallbackLanguage = preferredLanguage === 'vi' ? 'en' : 'vi';

  if (typeof value[preferredLanguage] === 'string' && value[preferredLanguage].trim()) {
    return value[preferredLanguage];
  }

  if (typeof value[fallbackLanguage] === 'string' && value[fallbackLanguage].trim()) {
    return value[fallbackLanguage];
  }

  return null;
};

const extractErrorMessage = async (response) => {
  const rawText = await response.text();

  if (!rawText) {
    return `Request failed with status ${response.status}`;
  }

  try {
    const parsed = JSON.parse(rawText);
    const localizedDetail = extractLocalizedMessage(parsed?.detail);
    if (localizedDetail) {
      return localizedDetail;
    }
    if (typeof parsed?.detail === 'string' && parsed.detail.trim()) {
      return parsed.detail;
    }
    const localizedMessage = extractLocalizedMessage(parsed?.message);
    if (localizedMessage) {
      return localizedMessage;
    }
    if (typeof parsed?.message === 'string' && parsed.message.trim()) {
      return parsed.message;
    }
  } catch (error) {
    // Fall back to the raw response body if it is not JSON.
  }

  return rawText;
};

const ensureOk = async (response) => {
  if (response.ok) {
    return response;
  }

  throw new Error(await extractErrorMessage(response));
};

class PortfolioTradingAPI {
  /**
   * Get all portfolios
   */
  async getAllPortfolios() {
    const response = await ensureOk(await fetch(`${API_BASE_URL}${API_PREFIX}/portfolios`));
    return response.json();
  }

  /**
   * Create a new portfolio
   */
  async createPortfolio(data) {
    const response = await ensureOk(await fetch(`${API_BASE_URL}${API_PREFIX}/portfolios`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }));
    return response.json();
  }

  /**
   * Get portfolio by ID
   */
  async getPortfolio(portfolioId) {
    const response = await ensureOk(await fetch(`${API_BASE_URL}${API_PREFIX}/portfolios/${portfolioId}`));
    return response.json();
  }

  /**
   * Get all positions for a portfolio
   */
  async getPositions(portfolioId) {
    const response = await ensureOk(await fetch(`${API_BASE_URL}${API_PREFIX}/portfolios/${portfolioId}/positions`));
    return response.json();
  }

  /**
   * Get lots for a position
   */
  async getPositionLots(portfolioId, ticker) {
    const response = await ensureOk(await fetch(`${API_BASE_URL}${API_PREFIX}/portfolios/${portfolioId}/positions/${ticker}/lots`));
    return response.json();
  }

  /**
   * Execute buy order
   */
  async buyStock(portfolioId, data) {
    const response = await ensureOk(await fetch(`${API_BASE_URL}${API_PREFIX}/portfolios/${portfolioId}/buy`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }));
    return response.json();
  }

  /**
   * Execute sell order
   */
  async sellStock(portfolioId, data) {
    const response = await ensureOk(await fetch(`${API_BASE_URL}${API_PREFIX}/portfolios/${portfolioId}/sell`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }));
    return response.json();
  }

  /**
   * Get sellable quantity
   */
  async getSellableQty(portfolioId, ticker) {
    const response = await ensureOk(await fetch(`${API_BASE_URL}${API_PREFIX}/portfolios/${portfolioId}/sellable-qty/${ticker}`));
    return response.json();
  }

  /**
   * Update NAV
   */
  async updateNAV(portfolioId, data) {
    const response = await ensureOk(await fetch(`${API_BASE_URL}${API_PREFIX}/portfolios/${portfolioId}/nav`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }));
    return response.json();
  }

  /**
   * Deposit/withdraw cash
   */
  async createCashTransaction(portfolioId, data) {
    const response = await ensureOk(await fetch(`${API_BASE_URL}${API_PREFIX}/portfolios/${portfolioId}/cash-transactions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data),
    }));
    return response.json();
  }

  /**
   * Get cash transactions
   */
  async getCashTransactions(portfolioId, days = 90) {
    const response = await ensureOk(await fetch(`${API_BASE_URL}${API_PREFIX}/portfolios/${portfolioId}/cash-transactions?days=${days}`, {
      headers: { ...getAuthHeaders() },
    }));
    return response.json();
  }

  /**
   * Wallet balance
   */
  async getWallet() {
    const response = await ensureOk(await fetch(`${API_BASE_URL}${API_PREFIX}/wallet`, {
      headers: { ...getAuthHeaders() },
    }));
    return response.json();
  }

  /**
   * Wallet topup/withdraw
   */
  async createWalletTransaction(data) {
    const response = await ensureOk(await fetch(`${API_BASE_URL}${API_PREFIX}/wallet/transactions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data),
    }));
    return response.json();
  }

  /**
   * Wallet transaction history
   */
  async getWalletTransactions(days = 30) {
    const response = await ensureOk(await fetch(`${API_BASE_URL}${API_PREFIX}/wallet/transactions?days=${days}`, {
      headers: { ...getAuthHeaders() },
    }));
    return response.json();
  }

  /**
   * Get NAV history
   */
  async getNAVHistory(portfolioId, days = 30) {
    const response = await ensureOk(await fetch(`${API_BASE_URL}${API_PREFIX}/portfolios/${portfolioId}/nav-history?days=${days}`));
    return response.json();
  }

  /**
   * Get performance summary
   */
  async getPerformance(portfolioId, days = 30) {
    const response = await ensureOk(await fetch(`${API_BASE_URL}${API_PREFIX}/portfolios/${portfolioId}/performance?days=${days}`));
    return response.json();
  }

  /**
   * Get weight-based model portfolio board
   */
  async getModelBoard(portfolioId, days = 90, actionLimit = 30) {
    const response = await ensureOk(await fetch(`${API_BASE_URL}${API_PREFIX}/portfolios/${portfolioId}/model-board?days=${days}&action_limit=${actionLimit}`));
    return response.json();
  }

  /**
   * Create signal
   */
  async createSignal(portfolioId, data) {
    const response = await ensureOk(await fetch(`${API_BASE_URL}${API_PREFIX}/portfolios/${portfolioId}/signals`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }));
    return response.json();
  }

  /**
   * Get signals
   */
  async getSignals(portfolioId, days = 30) {
    const response = await ensureOk(await fetch(`${API_BASE_URL}${API_PREFIX}/portfolios/${portfolioId}/signals?days=${days}`));
    return response.json();
  }

  /**
   * Get signal accuracy
   */
  async getSignalAccuracy(portfolioId, signalType = null, horizon = 10) {
    let url = `${API_BASE_URL}${API_PREFIX}/portfolios/${portfolioId}/signal-accuracy?horizon=${horizon}`;
    if (signalType) url += `&signal_type=${signalType}`;
    const response = await ensureOk(await fetch(url));
    return response.json();
  }

  /**
   * Get risk alerts
   */
  async getRiskAlerts(portfolioId, priceMap) {
    const response = await ensureOk(await fetch(`${API_BASE_URL}${API_PREFIX}/portfolios/${portfolioId}/risk-alerts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ price_map: priceMap }),
    }));
    return response.json();
  }

  /**
   * Run EOD workflow
   */
  async runEODWorkflow(portfolioId, data) {
    const response = await ensureOk(await fetch(`${API_BASE_URL}${API_PREFIX}/portfolios/${portfolioId}/run-eod`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }));
    return response.json();
  }

  /**
   * Check if date is trading day
   */
  async isTradingDay(date) {
    const response = await ensureOk(await fetch(`${API_BASE_URL}${API_PREFIX}/trading-calendar/is-trading-day?check_date=${date}`));
    return response.json();
  }

  /**
   * Calculate T+2 date
   */
  async getT2Date(buyDate) {
    const response = await ensureOk(await fetch(`${API_BASE_URL}${API_PREFIX}/trading-calendar/t2-date?buy_date=${buyDate}`));
    return response.json();
  }
  /**
   * Get current market price for a stock
   */
  async getCurrentPrice(ticker) {
    const response = await ensureOk(await fetch(`${API_BASE_URL}${API_PREFIX}/stocks/${ticker}/current-price`));
    return response.json();
  }

  /**
   * Refresh latest prices for all positions in a portfolio
   */
  async refreshPositionPrices(portfolioId) {
    const response = await ensureOk(await fetch(`${API_BASE_URL}${API_PREFIX}/portfolios/${portfolioId}/refresh-prices`, {
      method: 'POST',
    }));
    return response.json();
  }
}

const portfolioTradingAPI = new PortfolioTradingAPI();
export default portfolioTradingAPI;
